import { computed, ref, watch, type Ref } from "vue";

import { Language } from "@core/composables/localization/formatters";
import { profileService } from "@core/services/profileService";

import { extend as i18nExtend, init as i18nInit, translate as i18nTranslate, type ValueDictionary } from "@common/i18n";

import {
  baseTranslationNamespaces,
  loadLanguage,
  loadLanguageNamespaces,
  type LanguageTag,
  type TranslationNamespace
} from "@/translations";

export { Language };

export interface LanguageOption {
  code: Language;
  flag: string;
  name: string;
  nativeName: string;
}

const languageToTagMap: Record<Language, LanguageTag> = {
  [Language.Norwegian]: "nb",
  [Language.English]: "en"
};

export const languageOptions: LanguageOption[] = [
  { code: Language.Norwegian, flag: "\uD83C\uDDF3\uD83C\uDDF4", name: "Norwegian", nativeName: "Norsk" },
  { code: Language.English, flag: "\uD83C\uDF10", name: "English", nativeName: "English" }
];

interface LanguageState {
  language: Ref<Language>;
  isLanguageLoading: Ref<boolean>;
  languageRevision: Ref<number>;
  loadedNamespacesByLanguage: Map<LanguageTag, Set<TranslationNamespace>>;
  baseLoadingPromises: Map<LanguageTag, Promise<void>>;
  activeTranslationLoads: number;
}

const createLanguageState = (): LanguageState => ({
  language: ref<Language>(Language.Norwegian),
  isLanguageLoading: ref<boolean>(false),
  languageRevision: ref(0),
  loadedNamespacesByLanguage: new Map<LanguageTag, Set<TranslationNamespace>>(),
  baseLoadingPromises: new Map<LanguageTag, Promise<void>>(),
  activeTranslationLoads: 0
});

let languageState: LanguageState | undefined;

const getLanguageState = (): LanguageState => (languageState ??= createLanguageState());

const sharedLanguageState = getLanguageState();

export const language = sharedLanguageState.language;
export const isLanguageLoading = sharedLanguageState.isLanguageLoading;
const languageRevision = sharedLanguageState.languageRevision;
export const languageSignal = computed(() => `${languageToTagMap[language.value]}-${languageRevision.value}`);

const updateLoadingState = (delta: number) => {
  sharedLanguageState.activeTranslationLoads = Math.max(0, sharedLanguageState.activeTranslationLoads + delta);
  isLanguageLoading.value = sharedLanguageState.activeTranslationLoads > 0;
};

const getLanguageTag = (lang: Language): LanguageTag => languageToTagMap[lang];

const setDocumentLanguage = (lang: Language) => {
  if (typeof document === "undefined") {
    return;
  }
  document.documentElement.lang = getLanguageTag(lang);
};

const resetLoadedNamespaces = (tag: LanguageTag) => {
  sharedLanguageState.loadedNamespacesByLanguage.set(tag, new Set(baseTranslationNamespaces));
};

async function loadAndInitLanguage(lang: Language, preloadNamespaces?: TranslationNamespace[]): Promise<void> {
  const tag = getLanguageTag(lang);

  const existingPromise = sharedLanguageState.baseLoadingPromises.get(tag);
  if (existingPromise) {
    await existingPromise;
    return;
  }

  const loadPromise = (async () => {
    updateLoadingState(1);
    try {
      const translations = await loadLanguage(tag);
      const namespaces = Array.isArray(preloadNamespaces) ? Array.from(new Set(preloadNamespaces)) : [];
      const namespaceDictionaries = namespaces.length > 0 ? await loadLanguageNamespaces(tag, namespaces) : [];

      i18nInit(translations);
      namespaceDictionaries.forEach(dictionary => i18nExtend(dictionary));

      resetLoadedNamespaces(tag);
      const nextLoaded = new Set(baseTranslationNamespaces);
      namespaces.forEach(namespace => nextLoaded.add(namespace));
      sharedLanguageState.loadedNamespacesByLanguage.set(tag, nextLoaded);
      languageRevision.value += 1;
    } finally {
      sharedLanguageState.baseLoadingPromises.delete(tag);
      updateLoadingState(-1);
    }
  })();

  sharedLanguageState.baseLoadingPromises.set(tag, loadPromise);
  await loadPromise;
}

export async function ensureTranslationNamespaces(namespaces: TranslationNamespace[]): Promise<void> {
  const tag = getLanguageTag(language.value);

  const pendingBase = sharedLanguageState.baseLoadingPromises.get(tag);
  if (pendingBase) {
    await pendingBase;
  } else if (!sharedLanguageState.loadedNamespacesByLanguage.has(tag)) {
    await loadAndInitLanguage(language.value);
  }

  const loaded = sharedLanguageState.loadedNamespacesByLanguage.get(tag) ?? new Set<TranslationNamespace>();
  const missing = namespaces.filter(namespace => !loaded.has(namespace));

  if (missing.length === 0) {
    return;
  }

  updateLoadingState(1);
  try {
    const dictionaries = await loadLanguageNamespaces(tag, missing);
    dictionaries.forEach(dictionary => i18nExtend(dictionary));
    const nextLoaded = new Set(loaded);
    missing.forEach(namespace => nextLoaded.add(namespace));
    sharedLanguageState.loadedNamespacesByLanguage.set(tag, nextLoaded);
    languageRevision.value += 1;
  } finally {
    updateLoadingState(-1);
  }
}

async function persistLanguagePreference(lang: Language) {
  const tag = getLanguageTag(lang);
  try {
    const result = await profileService.setLanguage(tag);
    if (!result.success) {
      console.warn("Failed to persist language preference", result.errorKey);
    }
  } catch (error) {
    console.error("Failed to persist language preference", error);
  }
}

watch(
  language,
  async (newLang, oldLang) => {
    const previousNamespaces =
      oldLang !== undefined ? sharedLanguageState.loadedNamespacesByLanguage.get(getLanguageTag(oldLang)) : undefined;
    const preload = previousNamespaces !== undefined ? Array.from(previousNamespaces) : undefined;
    await loadAndInitLanguage(newLang, preload);
    await persistLanguagePreference(newLang);
  },
  { immediate: false }
);

export async function initializeLanguage(lang: Language): Promise<void> {
  await loadAndInitLanguage(lang);
}

export const normalizeLanguageTag = (value: string | null | undefined): Language => {
  if (!value) {
    return Language.English;
  }
  const normalized = value.trim().toLowerCase();
  if (normalized === "nb" || normalized === "no") {
    return Language.Norwegian;
  }
  if (normalized === "en") {
    return Language.English;
  }
  return Language.English;
};

const normalizeLanguageInput = (value: string | null | undefined): Language => {
  if (value === Language.Norwegian || value === Language.English) {
    return value;
  }
  if (typeof value === "string") {
    return normalizeLanguageTag(value);
  }
  return Language.English;
};

export function useLanguage() {
  const currentLanguage = computed(
    () => languageOptions.find(opt => opt.code === language.value) ?? languageOptions[0]
  );

  const setLanguage = (lang: string | null | undefined) => {
    const resolved = normalizeLanguageInput(lang);
    if (resolved === Language.English && lang != null && normalizeLanguageInput(lang) !== Language.English) {
      console.warn(`Unsupported language tag: ${lang}, defaulting to 'en'`);
    }
    setDocumentLanguage(resolved);
    if (language.value === resolved) {
      return;
    }
    language.value = resolved;
  };

  const toLanguageTag = (lang: Language): LanguageTag => getLanguageTag(lang);
  const t = (key: string, values?: ValueDictionary) => {
    return computed(() => {
      void language.value;
      return i18nTranslate(key, values);
    });
  };

  return {
    language,
    currentLanguage,
    languageOptions,
    setLanguage,
    toLanguageTag,
    normalizeLanguageTag,
    languageSignal,
    isLanguageLoading,
    t
  };
}
